import React, { useState, useEffect } from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CodePlayground = () => {
  const [selectedFramework, setSelectedFramework] = useState('react');
  const [selectedExample, setSelectedExample] = useState('component');
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);

  const frameworks = [
    { value: 'react', label: 'React', icon: 'Atom' },
    { value: 'vue', label: 'Vue.js', icon: 'Triangle' },
    { value: 'angular', label: 'Angular', icon: 'Square' },
    { value: 'svelte', label: 'Svelte', icon: 'Circle' },
    { value: 'vanilla', label: 'Vanilla JS', icon: 'Code' }
  ];

  const examples = {
    react: {
      component: {
        name: 'Interactive Component',
        code: `import React, { useState } from 'react';

const CyberButton = () => {
  const [clicks, setClicks] = useState(0);
  const [glowing, setGlowing] = useState(false);

  const handleClick = () => {
    setClicks(prev => prev + 1);
    setGlowing(true);
    setTimeout(() => setGlowing(false), 300);
  };

  return (
    <div className="p-8 bg-gray-900 rounded-lg">
      <button
        onClick={handleClick}
        className={\`px-6 py-3 bg-cyan-500 text-white rounded-lg 
                   transition-all duration-300 hover:bg-cyan-400
                   \${glowing ? 'shadow-lg shadow-cyan-500/50' : ''}\`}
      >
        Cyber Click ({clicks})
      </button>
      
      {clicks > 0 && (
        <div className="mt-4 text-cyan-400">
          Energy Level: {clicks * 10}%
        </div>
      )}
    </div>
  );
};

export default CyberButton;`
      },
      hooks: {
        name: 'Custom Hook',
        code: `import { useState, useEffect } from 'react';

// Custom hook for cyber-themed animations
const useCyberAnimation = (duration = 2000) => {
  const [isActive, setIsActive] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isActive) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            setIsActive(false);
            return 0;
          }
          return prev + (100 / (duration / 50));
        });
      }, 50);

      return () => clearInterval(interval);
    }
  }, [isActive, duration]);

  const startAnimation = () => {
    setIsActive(true);
    setProgress(0);
  };

  return { progress, isActive, startAnimation };
};

// Usage Example
const AnimatedComponent = () => {
  const { progress, isActive, startAnimation } = useCyberAnimation(3000);

  return (
    <div className="p-6 bg-gray-900 rounded-lg">
      <button
        onClick={startAnimation}
        className="px-4 py-2 bg-purple-600 text-white rounded"
        disabled={isActive}
      >
        {isActive ? 'Animating...' : 'Start Animation'}
      </button>
      
      <div className="mt-4 w-full bg-gray-700 rounded-full h-2">
        <div
          className="bg-gradient-to-r from-cyan-500 to-purple-500 h-2 rounded-full transition-all duration-100"
          style={{ width: \`\${progress}%\` }}
        />
      </div>
      
      <div className="mt-2 text-cyan-400 font-mono">
        Progress: {Math.round(progress)}%
      </div>
    </div>
  );
};`
      }
    },
    vue: {
      component: {
        name: 'Vue Component',
        code: `<template>
  <div class="p-8 bg-gray-900 rounded-lg">
    <button
      @click="handleClick"
      :class="buttonClasses"
      class="px-6 py-3 bg-green-500 text-white rounded-lg transition-all duration-300"
    >
      Vue Power ({{ clicks }})
    </button>
    
    <div v-if="clicks > 0" class="mt-4 text-green-400">
      Vue Energy: {{ clicks * 15 }}%
    </div>
    
    <div class="mt-4 w-full bg-gray-700 rounded-full h-2">
      <div
        class="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full transition-all duration-500"
        :style="{ width: energyWidth }"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'VueCyberButton',
  data() {
    return {
      clicks: 0,
      glowing: false
    }
  },
  computed: {
    buttonClasses() {
      return {
        'shadow-lg shadow-green-500/50': this.glowing,
        'hover:bg-green-400': !this.glowing
      }
    },
    energyWidth() {
      return Math.min(this.clicks * 10, 100) + '%'
    }
  },
  methods: {
    handleClick() {
      this.clicks++
      this.glowing = true
      setTimeout(() => {
        this.glowing = false
      }, 300)
    }
  }
}
</script>`
      }
    },
    vanilla: {
      component: {
        name: 'Vanilla JavaScript',
        code: `// Cyber-themed Interactive Element
class CyberInterface {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    this.clicks = 0;
    this.init();
  }

  init() {
    this.container.innerHTML = \`
      <div class="cyber-panel">
        <button id="cyberBtn" class="cyber-button">
          Initialize System (0)
        </button>
        <div id="statusBar" class="status-bar">
          <div class="status-fill"></div>
        </div>
        <div id="output" class="output-display">
          System Status: Standby
        </div>
      </div>
    \`;

    this.button = this.container.querySelector('#cyberBtn');
    this.statusFill = this.container.querySelector('.status-fill');
    this.output = this.container.querySelector('#output');

    this.button.addEventListener('click', () => this.handleClick());
    this.applyStyles();
  }

  handleClick() {
    this.clicks++;
    this.button.textContent = \`System Active (\${this.clicks})\`;
    
    // Animate status bar
    const progress = Math.min(this.clicks * 10, 100);
    this.statusFill.style.width = progress + '%';
    
    // Update output
    this.output.textContent = \`Power Level: \${progress}% | Clicks: \${this.clicks}\`;
    
    // Add glow effect
    this.button.classList.add('glowing');
    setTimeout(() => {
      this.button.classList.remove('glowing');
    }, 300);
  }

  applyStyles() {
    const style = document.createElement('style');
    style.textContent = \`
      .cyber-panel {
        padding: 2rem;
        background: #1a1a1a;
        border-radius: 0.5rem;
        border: 1px solid #00d4ff;
      }
      .cyber-button {
        padding: 0.75rem 1.5rem;
        background: #00d4ff;
        color: white;
        border: none;
        border-radius: 0.5rem;
        cursor: pointer;
        transition: all 0.3s ease;
      }
      .cyber-button:hover {
        background: #00b8e6;
        box-shadow: 0 0 20px rgba(0, 212, 255, 0.5);
      }
      .cyber-button.glowing {
        box-shadow: 0 0 30px rgba(0, 212, 255, 0.8);
      }
      .status-bar {
        width: 100%;
        height: 0.5rem;
        background: #333;
        border-radius: 0.25rem;
        margin: 1rem 0;
        overflow: hidden;
      }
      .status-fill {
        height: 100%;
        background: linear-gradient(90deg, #00d4ff, #8844ff);
        width: 0%;
        transition: width 0.5s ease;
      }
      .output-display {
        color: #00ff88;
        font-family: monospace;
        font-size: 0.875rem;
      }
    \`;
    document.head.appendChild(style);
  }
}

// Initialize the cyber interface
const cyberInterface = new CyberInterface('playground-output');`
      }
    }
  };

  const exampleOptions = selectedFramework && examples[selectedFramework] 
    ? Object.entries(examples[selectedFramework]).map(([key, value]) => ({
        value: key,
        label: value.name
      }))
    : [];

  useEffect(() => {
    if (selectedFramework && selectedExample && examples[selectedFramework]?.[selectedExample]) {
      setCode(examples[selectedFramework][selectedExample].code);
    }
  }, [selectedFramework, selectedExample]);

  const runCode = async () => {
    setIsRunning(true);
    
    // Simulate code execution
    setTimeout(() => {
      if (selectedFramework === 'vanilla') {
        setOutput(`<div id="playground-output"></div>
<script>
${code}
</script>`);
      } else {
        setOutput(`// Code executed successfully!
// Framework: ${frameworks.find(f => f.value === selectedFramework)?.label}
// Example: ${examples[selectedFramework]?.[selectedExample]?.name}

console.log('Code is running in ${selectedFramework} environment');
// Output would be rendered in a real environment`);
      }
      setIsRunning(false);
    }, 1500);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code);
  };

  const resetCode = () => {
    if (selectedFramework && selectedExample && examples[selectedFramework]?.[selectedExample]) {
      setCode(examples[selectedFramework][selectedExample].code);
    }
  };

  return (
    <div className="glass-morphism p-8 rounded-2xl cyber-border">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="font-orbitron font-bold text-2xl text-foreground mb-2 flex items-center space-x-3">
            <Icon name="Terminal" size={24} color="#00d4ff" />
            <span>Code Playground</span>
          </h3>
          <p className="text-text-secondary">
            Experiment with different technologies and see them in action
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-brand-green rounded-full animate-pulse"></div>
          <span className="text-xs font-mono text-brand-green">LIVE</span>
        </div>
      </div>

      {/* Controls */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Select
          label="Framework"
          options={frameworks}
          value={selectedFramework}
          onChange={setSelectedFramework}
          className="mb-0"
        />
        <Select
          label="Example"
          options={exampleOptions}
          value={selectedExample}
          onChange={setSelectedExample}
          disabled={!selectedFramework}
          className="mb-0"
        />
      </div>

      {/* Code Editor */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-exo font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Code" size={16} color="#00d4ff" />
              <span>Code Editor</span>
            </h4>
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                size="xs"
                iconName="Copy"
                iconSize={12}
                onClick={copyCode}
              />
              <Button
                variant="ghost"
                size="xs"
                iconName="RotateCcw"
                iconSize={12}
                onClick={resetCode}
              />
            </div>
          </div>
          
          <div className="relative">
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full h-96 bg-background/80 border border-brand-cyan/30 rounded-lg p-4 text-sm font-mono text-foreground resize-none focus:outline-none focus:border-brand-cyan"
              placeholder="Enter your code here..."
            />
            <div className="absolute top-2 right-2 text-xs text-text-tertiary font-mono">
              {selectedFramework?.toUpperCase()}
            </div>
          </div>

          <Button
            variant="default"
            fullWidth
            loading={isRunning}
            iconName="Play"
            iconPosition="left"
            iconSize={16}
            onClick={runCode}
            disabled={!code.trim()}
          >
            {isRunning ? 'Executing...' : 'Run Code'}
          </Button>
        </div>

        {/* Output */}
        <div className="space-y-4">
          <h4 className="font-exo font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Monitor" size={16} color="#00ff88" />
            <span>Output</span>
          </h4>
          
          <div className="relative">
            <div className="w-full h-96 bg-background/80 border border-brand-green/30 rounded-lg p-4 overflow-auto">
              {isRunning ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 border-2 border-brand-cyan border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-brand-cyan font-mono">Executing code...</span>
                  </div>
                </div>
              ) : output ? (
                <pre className="text-sm font-mono text-text-secondary whitespace-pre-wrap">
                  {output}
                </pre>
              ) : (
                <div className="flex items-center justify-center h-full text-text-tertiary">
                  <div className="text-center">
                    <Icon name="Play" size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Click "Run Code" to see output</p>
                  </div>
                </div>
              )}
            </div>
            <div className="absolute top-2 right-2 text-xs text-text-tertiary font-mono">
              OUTPUT
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 pt-6 border-t border-border">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            iconSize={14}
            onClick={() => {
              const blob = new Blob([code], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${selectedFramework}-${selectedExample}.${selectedFramework === 'vue' ? 'vue' : 'js'}`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Download
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Share"
            iconPosition="left"
            iconSize={14}
            onClick={() => {
              const shareData = {
                title: 'Code Playground',
                text: `Check out this ${selectedFramework} code example`,
                url: window.location.href
              };
              if (navigator.share) {
                navigator.share(shareData);
              } else {
                navigator.clipboard.writeText(window.location.href);
              }
            }}
          >
            Share
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            iconPosition="left"
            iconSize={14}
            onClick={() => window.open('https://codesandbox.io', '_blank')}
          >
            Open in CodeSandbox
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CodePlayground;